import { useId } from 'react';
import { Controller, type FieldValues, type UseFormReturn, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Section } from '@/shared/ui/layout/Section';
import { cn } from '@/shared/utils/cn';

// Reason the checkbox is currently disabled. ``null`` -> enabled.
// Maps to the three hint strings in i18n (`emptyTrekId`,
// `notInWarehouse`, `inPackage`).
export type ConfirmedShippingDisabledReason =
  | 'no-trek-id'
  | 'not-in-warehouse'
  | 'in-package'
  | null;

type Props<T extends FieldValues> = {
  form: UseFormReturn<T>;
  disabledReason: ConfirmedShippingDisabledReason;
  // ``true`` while the blur-fired warehouse probe is in flight. Used to
  // surface a tiny spinner / hint so the operator knows we're checking.
  loading?: boolean;
};

// Generic over any form values that include ``confirmed_for_shipping`` so
// both the parcel-create and parcel-edit widgets can render it without a
// duplicate component.
export const FeaturesParcelFormSectionConfirmedShipping = <T extends FieldValues>({
  form,
  disabledReason,
  loading,
}: Props<T>) => {
  const { t } = useTranslation();
  const inputId = useId();
  const disabled = disabledReason !== null;

  const hint = (() => {
    if (loading) return t('parcels.form.confirmShipping.checking');
    switch (disabledReason) {
      case 'no-trek-id':
        return t('parcels.form.confirmShipping.disabled.emptyTrekId');
      case 'not-in-warehouse':
        return t('parcels.form.confirmShipping.disabled.notInWarehouse');
      case 'in-package':
        return t('parcels.form.confirmShipping.disabled.inPackage');
      default:
        return t('parcels.form.confirmShipping.hint');
    }
  })();

  return (
    <Section title={t('parcels.form.sections.confirmShipping')}>
      <Controller
        control={form.control}
        name={'confirmed_for_shipping' as Path<T>}
        render={({ field }) => (
          <label
            htmlFor={inputId}
            className={cn(
              'flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 transition-colors',
              disabled
                ? 'cursor-not-allowed opacity-70'
                : 'cursor-pointer hover:border-brand-300',
            )}
          >
            <input
              id={inputId}
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed"
              checked={Boolean(field.value)}
              disabled={disabled}
              onChange={(e) => field.onChange(e.target.checked)}
              data-cy="parcel-confirmed-for-shipping"
            />
            <div className="flex flex-1 flex-col gap-1">
              <span className="text-sm font-medium text-ink">
                {t('parcels.form.confirmShipping.label')}
              </span>
              <span className="text-xs text-ink-muted">{hint}</span>
            </div>
          </label>
        )}
      />
    </Section>
  );
};
